/**
 * ESTADÍSTICAS DE HORAS — la pantalla
 *
 * Las mismas horas, cortadas de cuatro maneras, con el rango de fechas arriba y
 * una descarga en Excel con los cuatro cortes.
 *
 * ═══ EL NÚMERO NO SE MUESTRA SIN SU PROCEDENCIA ═══
 *
 * Abajo de cada tabla va qué se contó y qué no: cuántos asientos entraron,
 * cuántos quedaron afuera porque el técnico marcó que discrepan del papel, y
 * cuántos no tienen fecha. Es la misma regla que el resto de la aplicación: un
 * total sin su procedencia es un número en el que hay que confiar sin motivo.
 *
 * ═══ DE DÓNDE SALEN EMPRESA Y CLIENTE ═══
 *
 * Del asiento. Los tres caminos que registran un vuelo —el alta manual de la
 * bitácora, el botón del resumen y el cierre del cronómetro— piden para quién se
 * voló y lo guardan con el asiento (`conOperacion`, en `src/domain/clientes.js`).
 *
 * Los vuelos cargados ANTES de eso, y los que se guarden sin elegir, no lo dicen:
 * van a un renglón `(sin empresa)` / `(sin cliente)` con todo adentro. Eso también
 * es un dato —el corte no puede repartirlos entre las filas sin inventar— y se
 * dice en pantalla en vez de dejar la tabla vacía.
 */
import { useMemo, useState } from "react";
import * as XLSX from "xlsx";

import {
  SIN_CLIENTE,
  SIN_EMPRESA,
  horasPorAeronave,
  horasPorCliente,
  horasPorEmpresa,
  horasPorPiloto,
  totalesDelPeriodo,
} from "../../domain/estadisticas.js";
import "./estadisticas.css";

const CORTES = [
  { clave: "aeronave", nombre: "Por aeronave" },
  { clave: "piloto", nombre: "Por piloto" },
  { clave: "empresa", nombre: "Por empresa" },
  { clave: "cliente", nombre: "Por cliente" },
];

const hoy = new Date();
const primerDiaDelAnio = `${hoy.getFullYear()}-01-01`;

/* Una tabla por corte, con la misma firma, para poder recorrerlas y poder
   exportar las cuatro sin repetir el código de cada una. */
function tablaDe(corte, { bitacora, aeronaves, pilotos, empresas, clientes, desde, hasta }) {
  const comun = { desde: desde || null, hasta: hasta || null, empresas, clientes };
  if (corte === "aeronave") return horasPorAeronave(bitacora, aeronaves, comun);
  if (corte === "piloto") return horasPorPiloto(bitacora, pilotos, comun);
  if (corte === "empresa") return horasPorEmpresa(bitacora, empresas, comun);
  return horasPorCliente(bitacora, clientes, comun);
}

export default function PanelEstadisticas({
  bitacora = [],
  aeronaves = [],
  pilotos = [],
  empresas = [],
  clientes = [],
}) {
  const [corte, setCorte] = useState("aeronave");
  const [desde, setDesde] = useState(primerDiaDelAnio);
  const [hasta, setHasta] = useState("");

  const datos = useMemo(
    () => ({ bitacora, aeronaves, pilotos, empresas, clientes, desde, hasta }),
    [bitacora, aeronaves, pilotos, empresas, clientes, desde, hasta],
  );

  const tabla = useMemo(() => tablaDe(corte, datos), [corte, datos]);
  const totales = useMemo(() => totalesDelPeriodo(bitacora, { desde: desde || null, hasta: hasta || null }), [bitacora, desde, hasta]);

  /* ═══ EL EXCEL LLEVA LOS CUATRO CORTES ═══

     Quien descarga un informe de horas casi siempre termina armando la planilla
     con los cuatro, así que van las cuatro hojas con el MISMO filtro. Una hoja
     por vez obligaría a descargar cuatro archivos y a acordarse del rango. */
  const descargar = () => {
    const libro = XLSX.utils.book_new();
    const filtro = [
      ["Filtro", desde || "sin desde", hasta || "sin hasta"],
      ["Generado", new Date().toISOString().slice(0, 16).replace("T", " ")],
      [],
    ];

    for (const c of CORTES) {
      const t = tablaDe(c.clave, datos);
      const filas = [
        ...filtro,
        [c.nombre],
        [],
        ["Nombre", "Horas", "Horas de motor", "Vuelos", "Días", "Aterrizajes"],
        ...t.filas.map((f) => [f.nombre, f.horas, f.horasMotor, f.vuelos, f.dias, f.aterrizajes]),
      ];
      if (c.clave === "cliente" && t.sinCliente) {
        filas.push([SIN_CLIENTE, t.sinCliente.horas, t.sinCliente.horasMotor, t.sinCliente.vuelos, t.sinCliente.dias, t.sinCliente.aterrizajes]);
      }
      filas.push(
        [],
        ["TOTAL", t.total.horas, t.total.horasMotor, t.total.vuelos, t.total.dias, t.total.aterrizajes],
        [],
        ["Asientos contados", t.procedencia.delPeriodo],
        ["Fuera por discrepancia", t.procedencia.excluidosPorDiscrepancia],
        ["Sin fecha", t.procedencia.sinFecha],
      );
      XLSX.utils.book_append_sheet(libro, XLSX.utils.aoa_to_sheet(filas), c.nombre.slice(4, 35));
    }

    const nombre = `horas-de-vuelo_${desde || "inicio"}${hasta ? `_a_${hasta}` : ""}.xlsx`;
    XLSX.writeFile(libro, nombre);
  };

  const filas = tabla.filas;

  return (
    <div className="est">
      <header className="est__cabecera">
        <div>
          <h2 className="est__titulo">Horas de vuelo</h2>
          <p className="est__nota">
            Los cuatro cortes son las mismas horas, contadas de otra forma: el total de cada uno
            coincide con los demás para el mismo período. Se puede comprobar abajo, en «Total».
          </p>
        </div>
        <button type="button" className="est__descarga hc-no-print" onClick={descargar}>
          Descargar Excel (los 4 cortes)
        </button>
      </header>

      <div className="est__controles hc-no-print">
        <div className="est__cortes" role="group" aria-label="Cortar por">
          {CORTES.map((c) => (
            <button
              key={c.clave}
              type="button"
              className={`est__corte${c.clave === corte ? " es-actual" : ""}`}
              aria-pressed={c.clave === corte}
              onClick={() => setCorte(c.clave)}
            >
              {c.nombre}
            </button>
          ))}
        </div>
        <label className="est__campo">
          <span>Desde</span>
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </label>
        <label className="est__campo">
          <span>Hasta</span>
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </label>
        {(desde || hasta) && (
          <button
            type="button"
            className="est__limpiar"
            onClick={() => { setDesde(""); setHasta(""); }}
          >
            Todo el libro
          </button>
        )}
      </div>

      {filas.length === 0 ? (
        <p className="est__vacio">
          No hay vuelos registrados en ese período.
          {totales.procedencia.sinFecha > 0
            ? ` Hay ${totales.procedencia.sinFecha} asiento(s) sin fecha que no se pueden ubicar en ningún rango.`
            : ""}
        </p>
      ) : (
        <div className="est__tabla-caja">
          <table className="est__tabla">
            <caption className="est__caption">
              {CORTES.find((c) => c.clave === corte).nombre}
              {desde || hasta ? ` · ${desde || "inicio"} a ${hasta || "hoy"}` : " · todo el libro"}
            </caption>
            <thead>
              <tr>
                <th scope="col">Nombre</th>
                <th scope="col">Horas</th>
                <th scope="col">Motor</th>
                <th scope="col">Vuelos</th>
                <th scope="col">Días</th>
                <th scope="col">Aterrizajes</th>
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => (
                <tr key={String(f.clave)}>
                  <th scope="row">{f.nombre}</th>
                  <td className="est__num">{f.horas.toFixed(1)}</td>
                  <td className="est__num">{f.horasMotor.toFixed(1)}</td>
                  <td className="est__num">{f.vuelos}</td>
                  <td className="est__num">{f.dias}</td>
                  <td className="est__num">{f.aterrizajes}</td>
                </tr>
              ))}
              {/* El vuelo sin cliente no se esconde: si no estuviera, las filas
                  no sumarían el total y no habría forma de saber por qué. */}
              {corte === "cliente" && tabla.sinCliente && (
                <tr className="est__fila--resto">
                  <th scope="row">{SIN_CLIENTE}</th>
                  <td className="est__num">{tabla.sinCliente.horas.toFixed(1)}</td>
                  <td className="est__num">{tabla.sinCliente.horasMotor.toFixed(1)}</td>
                  <td className="est__num">{tabla.sinCliente.vuelos}</td>
                  <td className="est__num">{tabla.sinCliente.dias}</td>
                  <td className="est__num">{tabla.sinCliente.aterrizajes}</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <th scope="row">Total</th>
                <td className="est__num">{tabla.total.horas.toFixed(1)}</td>
                <td className="est__num">{tabla.total.horasMotor.toFixed(1)}</td>
                <td className="est__num">{tabla.total.vuelos}</td>
                <td className="est__num">{tabla.total.dias}</td>
                <td className="est__num">{tabla.total.aterrizajes}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* La procedencia del número, siempre visible. */}
      <p className="est__procedencia">
        Asientos contados: <strong>{tabla.procedencia.delPeriodo}</strong> ·
        fuera por discrepancia con el papel: <strong>{tabla.procedencia.excluidosPorDiscrepancia}</strong> ·
        sin fecha: <strong>{tabla.procedencia.sinFecha}</strong>
        {corte === "empresa" && filas.some((f) => f.clave === SIN_EMPRESA) && (
          <span className="est__falta">
            {" "}— Estos vuelos no dicen para qué empresa se volaron: son los cargados antes de que
            el registro lo pidiera, o los que se guardaron sin elegir. El corte los muestra aparte
            para que las filas sumen el total.
          </span>
        )}
        {corte === "cliente" && (tabla.sinCliente?.vuelos || 0) > 0 && (
          <span className="est__falta">
            {" "}— Vuelos sin cliente: los que no fueron para ninguno —traslado propio o
            instrucción— y los cargados antes de que el registro lo pidiera.
          </span>
        )}
      </p>
    </div>
  );
}
